import './styles/ManageCompanies.scss';

import { CircularProgress, Grid, Skeleton } from '@mui/material';

import { useQueryState } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Waypoint } from 'react-waypoint';

import { useJobmarketCompaniesQuery } from 'api/hooks/jobmarket/companies';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { FormWrapperModal } from 'app/components/FormWrapperModal';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { CompanyItem } from 'app/components/RichList/items/CompanyItem';
import { FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import { CompanyDetail } from '../CompanyDetail';
import { FilterHeader, SortDirection, SortField } from './FilterHeader';

export function ManageCompanies() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { user: currentUser } = useAuthContext();

  const aclHelper = new ACLHelper(currentUser);

  const isAdminUser = aclHelper.isJobmarketAdmin();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [numberOfCompaniesShown, setNumberOfCompaniesShown] = useState(15);
  const [filters, setFilters] = useState(
    isAdminUser
      ? {
          active: true,
          inactive: true,
          banned: false
        }
      : { active: true, inactive: false, banned: false }
  );
  // Sort state
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const nameInputState = useQueryState('name', { defaultValue: '', shallow: false });

  const stateFilter = Object.keys(filters).filter(key => filters[key]);

  const {
    data: companies,
    isFetching,
    isFetched,
    isLoading
  } = useJobmarketCompaniesQuery({
    params: {
      limit: numberOfCompaniesShown,
      offset: 0,
      state: stateFilter,
      name: nameInputState[0],
      sort: sortField,
      sortOrder: sortDirection
    }
  });

  usePageHeading({
    desktopOnly: true,
    className: 'manage-companies',
    heading: isAdminUser
      ? t(translations.layout.menu.manageCompanies())
      : t(translations.common.companies())
  });

  return (
    <>
      <ErrorBoundary boundaryTag="companyDetailBoundary">
        <FormWrapperModal
          className="company-manage-modal"
          open={createModalOpen}
          onClose={(props, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCreateModalOpen(false);
          }}
        >
          <div className="company-manage-modal__body">
            <CompanyDetail
              newCompany
              closeHandler={() => {
                setCreateModalOpen(false);
                dispatch(FormErrorActions.close());
              }}
            />
          </div>
        </FormWrapperModal>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="companyListBoundary">
        {isAdminUser && (
          <FilterHeader
            filters={filters}
            setFilters={setFilters}
            handleCreateClick={() => setCreateModalOpen(true)}
            search={nameInputState[0]}
            setSearch={nameInputState[1]}
            sortField={sortField}
            setSortField={setSortField}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
          />
        )}
        <RichList>
          {companies?.data.length === 0 ? (
            <NoItems />
          ) : (
            <>
              {isLoading &&
                Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} height={130} sx={{ transform: 'none', marginBottom: 2 }} />
                ))}
              {companies?.data.map(c => (
                <ErrorBoundary boundaryTag="itemBoundary" key={c.id}>
                  <CompanyItem studentView={!isAdminUser} company={c} />
                </ErrorBoundary>
              ))}
              {isFetched && companies?.data.length === numberOfCompaniesShown && (
                <Waypoint
                  bottomOffset={'-3%'}
                  onEnter={() => {
                    setNumberOfCompaniesShown(offset => offset + numberOfCompaniesShown);
                  }}
                />
              )}
              {isFetching && (
                <Grid container justifyContent="center">
                  <CircularProgress />
                </Grid>
              )}
            </>
          )}
        </RichList>
      </ErrorBoundary>
    </>
  );
}
